import { Component, Input } from '@angular/core';
import { AbstractControl, FormControl, ValidationErrors } from '@angular/forms';

/** bcrypt (the stored format) reads only 72 bytes; the backend refuses more. */
const MAX_BYTES = 72;
export const MIN_PASSWORD = 8;

/** The rules the backend checks that can be checked here too. */
export function passwordRules(control: AbstractControl): ValidationErrors | null {
  const value: string = control.value || '';
  if (!value) return null; // `required` reports an empty field
  const errors: ValidationErrors = {};
  if (value.length < MIN_PASSWORD) errors['tooShort'] = true;
  if (/^\d+$/.test(value)) errors['onlyNumbers'] = true;
  if (new TextEncoder().encode(value).length > MAX_BYTES) errors['tooLong'] = true;
  return Object.keys(errors).length ? errors : null;
}

/** 0-4: a rough guide shown as a meter, not a rule. */
export function passwordScore(value: string): number {
  if (!value || value.length < MIN_PASSWORD) return value ? 1 : 0;
  let score = 1;
  if (value.length >= 12) score++;
  const kinds = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((re) => re.test(value)).length;
  if (kinds >= 2) score++;
  if (kinds >= 3 && value.length >= 10) score++;
  if (/^(.)\1+$/.test(value) || /^(?:password|qwerty|letmein|welcome)/i.test(value)) score = 1;
  return Math.min(score, 4);
}

/**
 * Password input with a show/hide button and, for new passwords, a strength
 * meter and the rules (ticked as they're met).
 */
@Component({
  selector: 'app-password-field',
  templateUrl: './password-field.component.html',
  styleUrls: ['../auth-form.scss', './password-field.component.scss'],
  standalone: false,
})
export class PasswordFieldComponent {
  @Input({ required: true }) control!: FormControl<string | null>;
  @Input() label = 'Password';
  @Input() inputId = 'password';
  @Input() autocomplete: 'current-password' | 'new-password' = 'current-password';
  /** New passwords: meter + rules. */
  @Input() showStrength = false;
  /** Error from the server for this field (e.g. "That password is too common"). */
  @Input() serverError: string | null = null;

  visible = false;

  get value(): string {
    return this.control.value || '';
  }

  get score(): number {
    return passwordScore(this.value);
  }

  get scoreLabel(): string {
    return ['', 'Weak', 'Fair', 'Good', 'Strong'][this.score];
  }

  get longEnough(): boolean {
    return this.value.length >= MIN_PASSWORD;
  }

  get hasLetter(): boolean {
    return !!this.value && !/^\d+$/.test(this.value);
  }

  get showErrors(): boolean {
    return this.control.invalid && (this.control.touched || this.control.dirty) && !!this.control.errors;
  }

  get errorText(): string | null {
    if (this.serverError) return this.serverError;
    if (!this.showErrors) return null;
    const e = this.control.errors || {};
    if (e['required']) return 'Enter a password.';
    if (!this.showStrength) return null;
    if (e['tooLong']) return 'Use 72 characters or fewer.';
    if (e['tooShort']) return `Use at least ${MIN_PASSWORD} characters.`;
    if (e['onlyNumbers']) return 'Use some letters too, not only numbers.';
    if (e['mismatch']) return "The passwords don't match.";
    return null;
  }

  toggle(): void {
    this.visible = !this.visible;
  }
}
